/**
 * ProfileWizard — Opret en profil
 *
 * Step 0: Landing — 3 store valg (Bruger / MC-klub / Andet)
 * Step 1: Andet — kategori-kort
 * Step 2: Underkategorier — MULTI-SELECT (vælg primær + ekstra)
 * Step 3: Forespørgsel — inline formular
 *
 * VIGTIG REGEL: Alle formularer er EKSISTERENDE. Wizard navigerer kun.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, User, Bike, Sparkles, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader } from './ui/dialog';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Category config — built via t() on every render ─────────────────────────

const getAndetCategories = (t) => [
  {
    id: 'mad-drikke',
    emoji: '🍽️',
    name: t('wizard.catMadDrikke'),
    description: t('wizard.catMadDrikkeDesc'),
    action: 'subcategory',
    subcategories: [
      { emoji: '☕', key: 'cafe',        name: t('wizard.subCafe'),        path: '/opret-profil?category=cafe'       },
      { emoji: '🍽️', key: 'restaurant',  name: t('wizard.subRestaurant'),  path: '/opret-profil?category=restaurant' },
      { emoji: '🍔', key: 'grill',       name: t('wizard.subGrill'),       path: '/opret-profil?category=grill'      },
      { emoji: '🍕', key: 'pizzeria',    name: t('wizard.subPizzeria'),    path: '/opret-profil?category=pizzeria'   },
      { emoji: '🍦', key: 'isbar',       name: t('wizard.subIsbar'),       path: '/opret-profil?category=isbar'      },
      { emoji: '🍺', key: 'bar',         name: t('wizard.subBar'),         path: '/opret-profil?category=bar'        },
      { emoji: '🥐', key: 'bageri',      name: t('wizard.subBageri'),      path: '/opret-profil?category=bageri'     },
    ],
  },
  {
    id: 'overnatning',
    emoji: '🛏️',
    name: t('wizard.catOvernatning'),
    description: t('wizard.catOvernatningDesc'),
    action: 'subcategory',
    subcategories: [
      { emoji: '🏕️', key: 'camping',      name: t('wizard.subCamping'),      path: '/opret-profil?category=camping&type=accommodation'     },
      { emoji: '🏨', key: 'hotel',         name: t('wizard.subHotel'),        path: '/opret-profil?category=hotel&type=accommodation'       },
      { emoji: '🛏️', key: 'bnb',          name: t('wizard.subBnb'),          path: '/opret-profil?category=bnb&type=accommodation'         },
      { emoji: '🏠', key: 'hostel',        name: t('wizard.subHostel'),       path: '/opret-profil?category=hostel&type=accommodation'      },
      { emoji: '🛖', key: 'shelter',       name: t('wizard.subShelter'),      path: '/opret-profil?category=shelter&type=accommodation'     },
      { emoji: '🚐', key: 'camperplads',   name: t('wizard.subCamperplads'), path: '/opret-profil?category=camperplads&type=accommodation' },
    ],
  },
  {
    id: 'mc',
    emoji: '🏍️',
    name: t('wizard.catMC'),
    description: t('wizard.catMCDesc'),
    action: 'subcategory',
    subcategories: [
      { emoji: '⛽', key: 'tankstation',    name: t('wizard.subTankstation'),   path: '/opret-profil?category=tankstation'    },
      { emoji: '🔧', key: 'vaerksted',      name: t('wizard.subVaerksted'),     path: '/opret-profil?category=vaerksted'      },
      { emoji: '🛍️', key: 'mc-butik',       name: t('wizard.subMcButik'),       path: '/opret-profil?category=mc-butik'       },
      { emoji: '🏍️', key: 'mc-forhandler',  name: t('wizard.subMcForhandler'), path: '/opret-profil?category=mc-forhandler'  },
    ],
  },
  {
    id: 'event',
    emoji: '📅',
    name: t('wizard.catEvent'),
    description: t('wizard.catEventDesc'),
    action: 'navigate',
    path: '/opret-event',
  },
  {
    id: 'meetup',
    emoji: '🤝',
    name: t('wizard.catMeetup'),
    description: t('wizard.catMeetupDesc'),
    action: 'navigate',
    path: '/opret-meetup',
  },
  {
    id: 'indkob',
    emoji: '🛒',
    name: t('wizard.catIndkob'),
    description: t('wizard.catIndkobDesc'),
    action: 'request',
  },
  {
    id: 'foresporgsel',
    emoji: '❓',
    name: t('wizard.catForesporgsel'),
    description: t('wizard.catForesporgselDesc'),
    action: 'request',
  },
];

// ─── Step 0: Landing ─────────────────────────────────────────────────────────

const Step0Landing = ({ onSelectUser, onSelectClub, onSelectAndet, t }) => (
  <div className="space-y-5">
    <div className="text-center mb-2">
      <h2 className="text-3xl font-black text-white tracking-tight mb-1">
        {t('wizard.createProfile')}
      </h2>
      <p className="text-zinc-400 text-sm">
        {t('wizard.selectWhatToCreate')}
      </p>
    </div>

    {/* Bruger */}
    <button
      onClick={onSelectUser}
      data-testid="wizard-choice-bruger"
      className="w-full p-5 rounded-2xl border-2 border-zinc-700/60 bg-zinc-800/40 hover:border-emerald-400/60 hover:bg-zinc-800/80 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-200 text-left group hover:-translate-y-0.5"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors shrink-0">
          <User className="w-7 h-7 text-blue-400" />
        </div>
        <div>
          <div className="text-xl font-bold text-white group-hover:text-emerald-300 transition-colors">
            {t('wizard.user')}
          </div>
          <div className="text-sm text-zinc-400 mt-0.5">
            {t('wizard.userDesc')}
          </div>
        </div>
      </div>
    </button>

    {/* MC-klub */}
    <button
      onClick={onSelectClub}
      data-testid="wizard-choice-klub"
      className="w-full p-5 rounded-2xl border-2 border-zinc-700/60 bg-zinc-800/40 hover:border-emerald-400/60 hover:bg-zinc-800/80 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-200 text-left group hover:-translate-y-0.5"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center group-hover:bg-orange-500/30 transition-colors shrink-0">
          <Bike className="w-7 h-7 text-orange-400" />
        </div>
        <div>
          <div className="text-xl font-bold text-white group-hover:text-emerald-300 transition-colors">
            {t('wizard.club')}
          </div>
          <div className="text-sm text-zinc-400 mt-0.5">
            {t('wizard.clubDesc')}
          </div>
        </div>
      </div>
    </button>

    {/* Andet */}
    <button
      onClick={onSelectAndet}
      data-testid="wizard-choice-andet"
      className="w-full p-5 rounded-2xl border-2 border-zinc-700/60 bg-zinc-800/40 hover:border-emerald-400/60 hover:bg-zinc-800/80 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-200 text-left group hover:-translate-y-0.5"
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500/30 transition-colors shrink-0">
          <Sparkles className="w-7 h-7 text-purple-400" />
        </div>
        <div>
          <div className="text-xl font-bold text-white group-hover:text-emerald-300 transition-colors">
            {t('wizard.other')}
          </div>
          <div className="text-sm text-zinc-400 mt-0.5">
            {t('wizard.otherDesc')}
          </div>
        </div>
      </div>
    </button>
  </div>
);

// ─── Step 1: Andet — Kategorier ───────────────────────────────────────────────

const Step1AndetCategories = ({ onSelect, onBack, t }) => {
  const andetCategories = getAndetCategories(t);
  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
        data-testid="back-to-landing"
      >
        <ChevronLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      <div className="text-center mb-2">
        <div className="text-4xl mb-2">✨</div>
        <h2 className="text-2xl font-black text-white tracking-tight mb-1">
          {t('wizard.otherTitle')}
        </h2>
        <p className="text-zinc-400 text-sm">
          {t('wizard.selectWhatToCreate')}
        </p>
      </div>

      <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
        {andetCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => onSelect(cat)}
            data-testid={`andet-cat-${cat.id}`}
            className="w-full p-4 rounded-xl border-2 text-left transition-all duration-150 group hover:-translate-y-0.5 border-zinc-700/50 bg-zinc-800/40 hover:border-emerald-400/50 hover:bg-zinc-800/70"
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl">{cat.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-base text-white group-hover:text-emerald-300 transition-colors">
                  {cat.name}
                </div>
                <div className="text-xs text-zinc-400 mt-0.5 truncate">{cat.description}</div>
              </div>
              <ChevronLeft className="w-4 h-4 text-zinc-600 group-hover:text-emerald-400 rotate-180 transition-colors shrink-0" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── Step 2: Underkategorier — multi-select ───────────────────────────────────

const Step2Subcategories = ({ category, onContinue, onBack, t }) => {
  const [selected, setSelected] = useState([]);

  const toggle = (sub) => {
    setSelected(prev => {
      const existsIdx = prev.findIndex(s => s.key === sub.key);
      if (existsIdx !== -1) return prev.filter(s => s.key !== sub.key);
      return [...prev, sub];
    });
  };

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
        data-testid="back-to-categories"
      >
        <ChevronLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      <div className="text-center mb-1">
        <div className="text-4xl mb-2">{category.emoji}</div>
        <h2 className="text-2xl font-black text-white tracking-tight mb-1">
          {category.name.toUpperCase()}
        </h2>
        <p className="text-zinc-400 text-xs">{t('wizard.multiSelectNote')}</p>
      </div>

      <div className="grid grid-cols-2 gap-2 max-h-[42vh] overflow-y-auto">
        {(category.subcategories || []).map((sub, i) => {
          const selIdx = selected.findIndex(s => s.key === sub.key);
          const isSelected = selIdx !== -1;
          const isPrimary = selIdx === 0;
          return (
            <button
              key={i}
              onClick={() => toggle(sub)}
              data-testid={`subcategory-${sub.key}`}
              className={`p-4 rounded-xl border text-left transition-all relative ${
                isPrimary
                  ? 'border-emerald-400 bg-emerald-500/15 shadow-md shadow-emerald-500/10'
                  : isSelected
                    ? 'border-emerald-400/50 bg-emerald-500/8'
                    : 'border-zinc-700/50 bg-zinc-800/40 hover:border-emerald-400/40 hover:bg-zinc-800/70 hover:-translate-y-0.5'
              }`}
            >
              {isSelected && (
                <span className={`absolute top-1.5 right-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                  isPrimary ? 'bg-emerald-500 text-black' : 'bg-emerald-500/25 text-emerald-300'
                }`}>
                  {isPrimary ? t('wizard.primaryLabel') : t('wizard.extraLabel')}
                </span>
              )}
              <div className="text-3xl mb-2">{sub.emoji}</div>
              <div className="text-sm font-semibold text-white">
                {sub.name}
              </div>
            </button>
          );
        })}
      </div>

      {selected.length > 0 ? (
        <button
          onClick={() => onContinue(selected)}
          data-testid="subcategory-continue-btn"
          className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm transition-colors flex items-center justify-center gap-2"
        >
          {t('wizard.continue')}
          {selected.length > 1 && (
            <span className="ml-1 text-xs opacity-75 font-normal">({selected.length})</span>
          )}
        </button>
      ) : (
        <p className="text-center text-zinc-500 text-xs py-1">{t('wizard.selectPrimary')}</p>
      )}
    </div>
  );
};

// ─── Main ProfileWizard ───────────────────────────────────────────────────────

export const ProfileWizard = ({ open, onClose, onCreateClub }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [step, setStep] = useState('landing');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Forespørgsel form state
  const [reqName, setReqName] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqDesc, setReqDesc] = useState('');
  const [reqSending, setReqSending] = useState(false);

  const handleClose = () => onClose();

  const handleUser = () => {
    handleClose();
    navigate('/register?type=user');
  };

  const handleClub = () => {
    handleClose();
    if (onCreateClub) onCreateClub();
  };

  const resetAndClose = () => {
    setStep('landing');
    setSelectedCategory(null);
    setReqName(''); setReqEmail(''); setReqDesc('');
    handleClose();
  };

  const handleSelectCategory = (cat) => {
    if (cat.action === 'navigate') {
      resetAndClose();
      navigate(cat.path);
    } else if (cat.action === 'request') {
      setSelectedCategory(cat);
      setStep('request');
    } else {
      setSelectedCategory(cat);
      setStep('sub');
    }
  };

  // Multi-select: selectedSubs = [{key, emoji, name, path}, ...]
  const handleSelectSubs = (selectedSubs) => {
    if (selectedSubs.length === 0) return;
    const primary = selectedSubs[0];
    const extras = selectedSubs.slice(1).map(s => s.key);
    const extraParam = extras.length > 0 ? `&extra=${extras.join(',')}` : '';
    resetAndClose();
    navigate(`${primary.path}${extraParam}`);
  };

  const handleSendRequest = async (e) => {
    e.preventDefault();
    if (!reqName.trim() || !reqEmail.trim() || !reqDesc.trim()) {
      toast.error(t('wizard.fillAllFields'));
      return;
    }
    setReqSending(true);
    try {
      const res = await fetch(`${API_URL}/api/creation-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: reqName.trim(), email: reqEmail.trim(), description: reqDesc.trim() }),
      });
      if (!res.ok) throw new Error('server error');
      toast.success(t('wizard.requestSent'));
      resetAndClose();
    } catch {
      toast.error(t('wizard.requestFailed'));
    } finally {
      setReqSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent
        className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[92vh] overflow-hidden p-0"
        aria-describedby="profile-wizard-desc"
      >
        <DialogHeader className="sr-only">
          <span id="profile-wizard-desc">{t('wizard.selectWhatToCreate')}</span>
        </DialogHeader>

        {/* NOTE: DialogContent already renders a close button — do NOT add another one here */}

        <div className="overflow-y-auto p-6 pt-5">
          {step === 'landing' && (
            <Step0Landing
              onSelectUser={handleUser}
              onSelectClub={handleClub}
              onSelectAndet={() => setStep('andet')}
              t={t}
            />
          )}

          {step === 'andet' && (
            <Step1AndetCategories
              onSelect={handleSelectCategory}
              onBack={() => setStep('landing')}
              t={t}
            />
          )}

          {step === 'sub' && selectedCategory && (
            <Step2Subcategories
              category={selectedCategory}
              onContinue={handleSelectSubs}
              onBack={() => setStep('andet')}
              t={t}
            />
          )}

          {step === 'request' && (
            <div className="space-y-4">
              <button
                onClick={() => setStep('andet')}
                className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
                data-testid="back-to-categories-from-request"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('common.back')}
              </button>

              <div className="text-center mb-2">
                <div className="text-4xl mb-2">❓</div>
                <h2 className="text-2xl font-black text-white tracking-tight mb-1">
                  {t('wizard.request')}
                </h2>
                <p className="text-zinc-400 text-sm">
                  {t('wizard.requestDesc')}
                </p>
              </div>

              <form onSubmit={handleSendRequest} className="space-y-3" data-testid="request-form">
                <div>
                  <label className="block text-xs font-semibold text-zinc-400 mb-1 uppercase tracking-wide">
                    {t('wizard.yourName')} *
                  </label>
                  <input
                    type="text"
                    value={reqName}
                    onChange={e => setReqName(e.target.value)}
                    placeholder={t('wizard.fullName')}
                    data-testid="request-name"
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-400 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-400 mb-1 uppercase tracking-wide">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    value={reqEmail}
                    onChange={e => setReqEmail(e.target.value)}
                    placeholder="din@email.dk"
                    data-testid="request-email"
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-400 transition-colors text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-zinc-400 mb-1 uppercase tracking-wide">
                    {t('wizard.whatToCreate')} *
                  </label>
                  <textarea
                    value={reqDesc}
                    onChange={e => setReqDesc(e.target.value)}
                    placeholder={t('wizard.whatToCreatePlaceholder')}
                    data-testid="request-description"
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:outline-none focus:border-emerald-400 transition-colors text-sm resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={reqSending}
                  data-testid="request-submit"
                  className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  {reqSending ? t('wizard.sending') : t('wizard.sendRequest')}
                </button>
              </form>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileWizard;
